import { useEffect, useRef, useState } from "react";
import { Col, Row, Input, Select, Button } from "antd";
import { BaseButtonsForm } from "components/common/forms/BaseButtonsForm";
import { notificationController } from "components/common/notificationController";
import SpinLoader from "components/Loader/SpinLoader";
import { useDispatch } from "react-redux";
import { config } from "config/site.config";
import {
  DIMENSION_UNIT_OPTION,
  WEIGHT_UNIT_OPTION,
} from "pages/protectedPages/ActivityLog/PdfForm";
import apiService from "services/apiservice";
import { getErrorMessageString } from "utils/utils";
import { addUpdatePiaggioItemDetailsRequest } from "redux/piaggioItemDetails/actions";
import { mlItemsString, stringConfig } from "config/site.string";
const { Option } = Select;

interface UnitsType {
  label: string;
  value: string;
}

interface IAddUpdateShipmentContainerForm {
  form: any;
  handleOk: any;
  isEdit: boolean;
  onClose: (isSuccess?: boolean) => void;
  handleCancel: () => void;
  selectedRecord: any;
}
export interface ContainerTypes {
  containerTypeCode: number | undefined;
  containerCode: string;
  containerTypeDescription: string;
}

export const EditPiaggioItemDetails = ({
  form,
  isEdit,
  onClose,
  selectedRecord,
}: IAddUpdateShipmentContainerForm) => {
  const dispatch = useDispatch();
  const initializedContainer = useRef(false);
  const initialized = useRef(false);
  const [containerTypes, setContainerTypes] = useState<ContainerTypes[]>();
  const [pending] = useState<boolean>(false);
  const currentUser = JSON.parse(
    localStorage.getItem("loggedInUser") || "null",
  );
  const token = localStorage.getItem("token");

  let weightTypes: UnitsType[] = WEIGHT_UNIT_OPTION;
  let dimensionTypes: UnitsType[] = DIMENSION_UNIT_OPTION;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await apiService.get(`/GetContainerTypes`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setContainerTypes(data);
      } catch (e: any) {
        const errorMessages = getErrorMessageString(e);
        if (errorMessages !== undefined && e?.response?.status !== 401)
          notificationController.error({
            message: "Error",
            description: `Error fetching container types: ${errorMessages}`,
          });
      }
    };
    if (!initialized.current) {
      initialized.current = true;
      fetchData();
    }
  }, []);

  useEffect(() => {
    if (!initializedContainer.current) {
      initializedContainer.current = true;

      if (!isEdit) {
        form.resetFields();
      } else {
        // Set form values for edit mode
        form.setFieldsValue({
          serialNumber: selectedRecord?.serialNumber,
          quantity: selectedRecord?.quantity,
          containerType: selectedRecord?.containerType,
          description: selectedRecord?.description,
          weight: selectedRecord?.weight,
          weightUnit: selectedRecord?.weightUnit || "lb",
          length: selectedRecord?.length,
          width: selectedRecord?.width,
          height: selectedRecord?.height,
          dimensionUnit: selectedRecord?.dimensionUnit || "in",
        });
      }
    }
  }, []);

  const addContainerCallback = (type: string, message: string) => {
    if (type === "Success") {
      notificationController.success({
        message: "Success",
        description: message
          ? message
          : `${
              isEdit
                ? `Shipment Container details updated successfully`
                : `Shipment Container details added successfully`
            }`,
        placement: "topRight",
      });
      form.resetFields();
      onClose(true);
    } else {
      notificationController.error({
        message: "Error",
        description: message ? message : "Sorry an error occurred",
        placement: "topRight",
      });
    }
  };

  const handleSubmit = (values: any) => {
    const containerValues: any = {
      id: isEdit ? selectedRecord?.id : null,
      serialNumber: values.serialNumber,
      quantity: values.quantity,
      containerType: values.containerType,
      description: values.description,
      weight: values.weight,
      weightUnit: values.weightUnit,
      length: values.length,
      width: values.width,
      height: values.height,
      dimensionUnit: values.dimensionUnit,
      createdBy: currentUser?.userDetails?.userId,
    };

    dispatch(
      addUpdatePiaggioItemDetailsRequest(
        config.addUpdatePiaggioItemDetails,
        containerValues,
        addContainerCallback,
      ),
    );
  };
  const SelectDimensionsDropdown = (
    dimensionTypes: UnitsType[] | undefined,
    value: string | null,
    onChange: ((value: string) => void) | undefined,
    placeholder: string,
  ) => {
    return (
      <Select
        style={{ width: "100%" }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      >
        {dimensionTypes?.map((type) => (
          <Option key={type.label} value={type.value}>
            {type.label}
          </Option>
        ))}
      </Select>
    );
  };

  const SelectWeightDropdown = (
    weightTypes: UnitsType[] | undefined,
    value: string | null,
    onChange: ((value: string) => void) | undefined,
    placeholder: string,
  ) => {
    return (
      <Select
        style={{ width: "100%" }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      >
        {weightTypes?.map((type) => (
          <Option key={type.label} value={type.value}>
            {type.label}
          </Option>
        ))}
      </Select>
    );
  };
  const SelectContainerDropdown = (
    containerTypes: ContainerTypes[] | undefined,
    value: string | null,
    onChange: ((value: string) => void) | undefined,
    placeholder: string,
  ) => {
    return (
      <Select
        style={{ width: "100%" }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      >
        {containerTypes?.map((type) => (
          <Option key={type.containerTypeCode} value={type.containerCode}>
            {type.containerTypeDescription}
          </Option>
        ))}
      </Select>
    );
  };

  const handleCancelButtonClick = () => {
    form.resetFields();
    onClose();
  };
  return (
    <>
      {pending ? (
        <SpinLoader />
      ) : (
        <BaseButtonsForm
          form={form}
          name="shipmentContainer"
          layout="horizontal"
          style={{ padding: "12px 20px 0px" }}
          onFinish={handleSubmit}
          id="shipmentContainer"
        >
          <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="serialNumber"
                label="Serial Number/Material Name"
                labelCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Serial number/Material Name is required",
                  },
                ]}
              >
                <Input disabled={isEdit} />
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="description"
                label={<>{mlItemsString.description}</>}
                labelCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Description is required",
                  },
                ]}
              >
                <Input />
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="quantity"
                label={<>{mlItemsString.quantity}</>}
                labelCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Quantity is required",
                  },
                ]}
              >
                <Input />
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="containerType"
                label={<>{mlItemsString.containerType}</>}
                labelCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Container is required",
                  },
                ]}
              >
                {SelectContainerDropdown(
                  containerTypes,
                  "crates",
                  undefined,
                  stringConfig.select,
                )}
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="weight"
                label={<>{mlItemsString.weight}</>}
                labelCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Weight is required",
                  },
                ]}
              >
                <Input />
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="weightUnit"
                label={<>{mlItemsString.weightUnit}</>}
                labelCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: "Weight unit is required",
                  },
                ]}
              >
                {SelectWeightDropdown(
                  weightTypes,
                  "lb",
                  undefined,
                  stringConfig.select,
                )}
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="length"
                label={<>{mlItemsString.length}</>}
                labelCol={{ span: 24 }}
                required={false}
              >
                <Input />
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="width"
                label={<>{mlItemsString.width}</>}
                labelCol={{ span: 24 }}
                required={false}
              >
                <Input />
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="height"
                label={<>{mlItemsString.height}</>}
                labelCol={{ span: 24 }}
                required={false}
              >
                <Input />
              </BaseButtonsForm.Item>
            </Col>
            <Col span={24}>
              <BaseButtonsForm.Item
                name="dimensionUnit"
                labelCol={{ span: 24 }}
                label={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>{mlItemsString.dimensionUnit}</div>
                    <div>{mlItemsString.dimensions}</div>
                  </div>
                }
                required={false}
              >
                {SelectDimensionsDropdown(
                  dimensionTypes,
                  "in",
                  undefined,
                  stringConfig.select,
                )}
              </BaseButtonsForm.Item>
            </Col>
          </Row>
          <div style={{ textAlign: "right", marginTop: 10 }}>
            <Button type="primary" htmlType="submit" style={{ marginRight: 5 }}>
              SUBMIT
            </Button>
            <Button type="primary" onClick={handleCancelButtonClick}>
              CANCEL
            </Button>
          </div>
        </BaseButtonsForm>
      )}
    </>
  );
};

export default EditPiaggioItemDetails;
